import React, { useState, useEffect, useRef } from 'react'
import { useFirestoreChat } from '../../../hooks/use_firestore_chat'
import { ChatRoom } from '../../../clients/kyousou_api_client'
import { useAuthAuthorized } from '../../../hooks/auth/use_auth'
import LoadingSpinner from '../../../components/loading/loading_spinner'
import { useNavigate } from 'react-router-dom'
import { ChatUserMessage } from '../components/chat_user_message'
import {
  FirestoreSystemMessage,
  FirestoreUserMessage,
  AttachmentInfo,
} from '../../../types/chat'
import { ChatSystemMessage } from '../components/chat_system_message'
import {
  ApiErrorResponse,
  getKyousouApiClient,
  handleError,
} from '../../../services/network'
import { useSnackBar } from '../../../providers/snack_bar_provider'
import { useLoading } from '../../../providers/loading_provider'
import { useImageUpload } from '../../../hooks/use_image_upload'
import { useFileUpload } from '../../../hooks/use_file_upload'

interface ChatDetailProps {
  chat: ChatRoom
  firebaseToken: string | null
  markAsRead: (chatRoomId: number) => Promise<void>
}

// 添付ファイルの最大数
const MAX_ATTACHMENTS_COUNT = 5

const ChatDetailSection: React.FC<ChatDetailProps> = (props) => {
  const [chat, setChat] = useState(props.chat)
  const [messageText, setMessageText] = useState('')
  const [isComposing, setIsComposing] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [previewUrls, setPreviewUrls] = useState<string[]>([])
  const [isMessagesVisible, setIsMessagesVisible] = useState(false)
  const currentUser = useAuthAuthorized()
  const navigate = useNavigate()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const isInitialScrollRef = useRef<boolean>(true)
  const hasMarkedAsReadRef = useRef<number | null>(null)
  const apiClient = getKyousouApiClient()
  const { showSnackBar } = useSnackBar()
  const { showLoading, hideLoading } = useLoading()
  const { upload: uploadImage } = useImageUpload()
  const { upload: uploadFile } = useFileUpload()

  useEffect(() => {
    setChat(props.chat)

    // チャットが変わったときに既読処理（重複防止用のRef使用）
    if (props.chat.id && hasMarkedAsReadRef.current !== props.chat.id) {
      props.markAsRead(props.chat.id)
      hasMarkedAsReadRef.current = props.chat.id
    }
  }, [props.chat.id, props.markAsRead])

  const {
    messages,
    loading,
    error,
    sendMessage,
    deleteMessage,
    isAuthenticated,
  } = useFirestoreChat(
    chat.id,
    chat.firebaseRoomId,
    props.firebaseToken,
    Number(currentUser.user.kuchikomiEnterpriseId),
    50, // 最大50件のメッセージを読み込み
    () => {
      // 新しいメッセージを受信したときの自動既読処理
      if (chat.id) {
        props.markAsRead(chat.id)
      }
    }
  )

  // 自動スクロール機能
  const scrollToBottom = () => {
    // 初回は即座にスクロール、2回目以降は滑らかにスクロール
    const behavior = isInitialScrollRef.current ? 'auto' : 'smooth'
    messagesEndRef.current?.scrollIntoView({ behavior })

    // 初回スクロール後はフラグをfalseに、メッセージを表示
    if (isInitialScrollRef.current) {
      isInitialScrollRef.current = false
      // スクロール完了後にメッセージを表示
      setTimeout(() => {
        setIsMessagesVisible(true)
      }, 0)
    }
  }

  // メッセージが更新されたら自動スクロール
  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // textareaの高さを自動調整
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      const scrollHeight = textareaRef.current.scrollHeight
      const maxHeight = 120 // 最大5行分程度
      textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`
    }
  }, [messageText])

  const handleSendMessage = async () => {
    // メッセージまたはファイルのいずれかが存在し、認証済みの場合のみ送信
    const hasText = messageText.trim().length > 0
    const hasFiles = selectedFiles.length > 0

    if ((hasText || hasFiles) && isAuthenticated) {
      const sendMessageText = messageText
      const filesToUpload = [...selectedFiles]

      try {
        setMessageText('')
        setSelectedFiles([])
        setPreviewUrls([])

        // ファイルをアップロード
        let attachments: AttachmentInfo[] = []
        if (filesToUpload.length > 0) {
          try {
            // 複数ファイルを順次アップロード
            const uploadPromises = filesToUpload.map((file) =>
              file.type.startsWith('image/')
                ? uploadImage(file)
                : uploadFile(file)
            )
            const results = await Promise.all(uploadPromises)

            // nullを除外
            attachments = results.filter(
              (result): result is AttachmentInfo => result !== null
            )

            if (attachments.length !== filesToUpload.length) {
              showSnackBar(
                ['一部のファイルのアップロードに失敗しました'],
                'error'
              )
              // エラー時は元に戻す
              setMessageText(sendMessageText)
              setSelectedFiles(filesToUpload)
              const newPreviewUrls = filesToUpload
                .filter((file) => file.type.startsWith('image/'))
                .map((file) => URL.createObjectURL(file))
              setPreviewUrls(newPreviewUrls)
              return
            }
          } catch (error) {
            console.error('File upload error:', error)
            showSnackBar(['ファイルのアップロードに失敗しました'], 'error')
            // エラー時は元に戻す
            setMessageText(sendMessageText)
            setSelectedFiles(filesToUpload)
            const newPreviewUrls = filesToUpload
              .filter((file) => file.type.startsWith('image/'))
              .map((file) => URL.createObjectURL(file))
            setPreviewUrls(newPreviewUrls)
            return
          }
        }

        // メッセージを送信
        await sendMessage(sendMessageText, attachments)
      } catch (err) {
        console.error('メッセージ送信エラー:', err)
      }
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Shift+Enterで改行（デフォルト動作のため処理不要）
    if (e.key === 'Enter' && e.shiftKey) {
      return
    }

    // Enterのみで送信（ただし日本語入力中は送信しない）
    if (e.key === 'Enter' && !isComposing) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])

    // ファイルサイズチェック (10MB以下)
    const invalidFiles = files.filter((file) => file.size > 10 * 1024 * 1024)
    if (invalidFiles.length > 0) {
      showSnackBar(['10MBを超えるファイルは送信できません'], 'error')
      return
    }

    // 最大数チェック
    if (selectedFiles.length + files.length > MAX_ATTACHMENTS_COUNT) {
      showSnackBar(
        [`ファイルは最大${MAX_ATTACHMENTS_COUNT}つまでです`],
        'error'
      )
      return
    }

    setSelectedFiles((prev) => [...prev, ...files])

    // プレビューURL生成（画像のみ）
    const newPreviewUrls = files
      .filter((file) => file.type.startsWith('image/'))
      .map((file) => URL.createObjectURL(file))
    setPreviewUrls((prev) => [...prev, ...newPreviewUrls])
  }

  const handleRemoveFile = (index: number) => {
    const file = selectedFiles[index]

    // 画像の場合はプレビューURLを解放
    if (file.type.startsWith('image/')) {
      const imageIndex = selectedFiles
        .slice(0, index)
        .filter((f) => f.type.startsWith('image/')).length
      if (previewUrls[imageIndex]) {
        URL.revokeObjectURL(previewUrls[imageIndex])
        setPreviewUrls((prev) => prev.filter((_, i) => i !== imageIndex))
      }
    }

    setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  // プレビューURLのクリーンアップ
  useEffect(() => {
    return () => {
      previewUrls.forEach((url) => URL.revokeObjectURL(url))
    }
  }, [])

  const declineMatchingRequest = (matchingRequestId: number) => {
    showLoading()

    apiClient.enterprise
      .matchingRequestDeclineCreate(
        chat.partnerId.toString(),
        matchingRequestId.toString()
      )
      .catch((e: ApiErrorResponse) => {
        handleError(e, showSnackBar)
        return null
      })
      .finally(() => {
        hideLoading()
      })
  }

  const acceptMatchingRequest = (matchingRequestId: number) => {
    showLoading()

    apiClient.enterprise
      .matchingRequestAcceptCreate(
        chat.partnerId.toString(),
        matchingRequestId.toString()
      )
      .catch((e: ApiErrorResponse) => {
        handleError(e, showSnackBar)
        return null
      })
      .finally(() => {
        hideLoading()
      })
  }

  return (
    <div className="h-full flex flex-col bg-main-white rounded-lg overflow-hidden">
      {/* ヘッダー */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-main-gray">
        <div className="flex items-center">
          <img
            src={chat.logo}
            alt={chat.name}
            className="w-10 h-10 rounded-full mr-3 object-cover"
          />
          <div>
            <h2 className="font-bold text-lg">{chat.name}</h2>
            <p className="text-sm text-main-darkgray">
              オンライン・最終アクティブ10分前
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate(`/enterprises/detail/${chat.partnerId}`)}
          className="px-4 py-2 border border-main-gray rounded-lg text-sm text-main-black bg-main-white hover:bg-main-lightgray transition-colors"
        >
          プロフィールを見る
        </button>
      </div>

      {/* メッセージエリア */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4 relative">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="text-2xl text-main-gray mb-2">
                <LoadingSpinner />
              </div>
              <p className="text-main-darkgray">メッセージを読み込み中...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="text-2xl text-main-red mb-2">
                <LoadingSpinner />
              </div>
              <p className="text-main-red">{error}</p>
            </div>
          </div>
        ) : (
          <>
            {/* 画面ちらつき防止のためのローディングオーバーレイ */}
            {!isMessagesVisible && (
              <div className="absolute inset-0 flex items-center justify-center bg-main-white z-10">
                <div className="text-center">
                  <div className="text-2xl text-main-gray mb-2">
                    <LoadingSpinner />
                  </div>
                  <p className="text-main-darkgray">
                    メッセージを読み込み中...
                  </p>
                </div>
              </div>
            )}
            <div>
              {messages.map((message) => {
                if (message.chatType === 'user') {
                  return (
                    <ChatUserMessage
                      key={message.id}
                      message={message as FirestoreUserMessage}
                      chat={chat}
                      currentUser={currentUser.user}
                      onDelete={deleteMessage}
                    />
                  )
                } else {
                  return (
                    <ChatSystemMessage
                      key={message.id}
                      message={message as FirestoreSystemMessage}
                      declineMatchingRequest={declineMatchingRequest}
                      acceptMatchingRequest={acceptMatchingRequest}
                    />
                  )
                }
              })}
              <div ref={messagesEndRef} />
            </div>
          </>
        )}
      </div>

      {/* メッセージ入力エリア */}
      <div className="px-6 py-4 border-t border-main-gray">
        {!isAuthenticated ? (
          <div className="text-center text-main-darkgray py-4">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="flex flex-col gap-2 mb-2">
            {/* ファイルプレビュー */}
            {selectedFiles.length > 0 && (
              <div className="flex gap-4">
                {/* 画像: サムネイル表示 */}
                {selectedFiles.filter((file) => file.type.startsWith('image/'))
                  .length > 0 && (
                  <>
                    {selectedFiles
                      .map((file, index) => ({ file, index }))
                      .filter(({ file }) => file.type.startsWith('image/'))
                      .map(({ file, index }) => {
                        const imageIndex = selectedFiles
                          .slice(0, index)
                          .filter((f) => f.type.startsWith('image/')).length

                        return (
                          <div key={`image-${index}`} className="relative">
                            <img
                              src={previewUrls[imageIndex]}
                              alt={`preview-${index}`}
                              className="w-20 h-20 object-cover rounded-lg border border-main-gray"
                            />
                            <button
                              onClick={() => handleRemoveFile(index)}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-main-red text-main-white rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
                            >
                              <span className="material-icons text-sm">
                                close
                              </span>
                            </button>
                          </div>
                        )
                      })}
                  </>
                )}

                {/* ファイル（画像以外） */}
                {selectedFiles
                  .filter((file) => !file.type.startsWith('image/'))
                  .map((file, index) => (
                    <div
                      key={`file-${index}`}
                      className="w-20 h-20 pt-3 px-1 text-center relative bg-main-lightgray border border-main-gray rounded-lg"
                    >
                      <span className="material-icons text-main-darkgray text-4xl">
                        description
                      </span>
                      <p className="text-xs text-main-black truncate relative -top-1">
                        {file.name}
                      </p>
                      <button
                        onClick={() =>
                          handleRemoveFile(selectedFiles.indexOf(file))
                        }
                        className="absolute -top-2 -right-2 w-6 h-6 bg-main-red text-main-white rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
                      >
                        <span className="material-icons text-xs">close</span>
                      </button>
                    </div>
                  ))}
              </div>
            )}

            <div className="flex items-end gap-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar,.txt,.csv,.ppt,.pptx,.mp4,.mov,.avi,.wmv,.webm"
                multiple
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={!isAuthenticated || chat.status === 'limited'}
                className="p-2 hover:bg-main-lightgray rounded-lg flex items-center justify-center"
              >
                <span className="material-icons text-main-darkgray">
                  attachment
                </span>
              </button>
              <textarea
                rows={1}
                ref={textareaRef}
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onKeyDown={handleKeyDown}
                onCompositionStart={() => setIsComposing(true)}
                onCompositionEnd={() => setIsComposing(false)}
                placeholder="Aa"
                className="flex-1 px-4 py-2 bg-main-lightgray rounded-2xl focus:outline-none focus:ring-2 focus:ring-main-green resize-none overflow-y-auto"
                disabled={!isAuthenticated || chat.status === 'limited'}
                style={{ minHeight: '20px' }}
              />
              <button
                onClick={handleSendMessage}
                disabled={
                  !isAuthenticated ||
                  (!messageText.trim() && selectedFiles.length === 0) ||
                  chat.status === 'limited'
                }
                className={`p-2 rounded-lg flex items-center justify-center ${
                  isAuthenticated &&
                  (messageText.trim() || selectedFiles.length > 0)
                    ? 'bg-main-green text-main-white hover:bg-main-green'
                    : 'bg-main-gray text-main-darkgray cursor-not-allowed'
                }`}
              >
                <span className="material-icons">send</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ChatDetailSection
